import { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faCircleCheck,
  faCircleExclamation,
  faTriangleExclamation,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./Modal.module.css";

/**
 * Componente Modal reutilizable
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Controla si el modal está visible
 * @param {function} props.onClose - Función para cerrar el modal
 * @param {string} props.title - Título del modal (opcional)
 * @param {React.ReactNode} props.children - Contenido del modal
 * @param {('success'|'error'|'warning'|'info'|'confirm')} props.type - Tipo de modal
 * @param {string} props.confirmText - Texto del botón de confirmar (default: "Confirmar")
 * @param {string} props.cancelText - Texto del botón de cancelar (default: "Cancelar")
 * @param {function} props.onConfirm - Función al confirmar (para tipo confirm)
 * @param {boolean} props.showIcon - Mostrar icono según el tipo (default: true)
 * @param {boolean} props.closeOnOverlay - Cerrar al hacer clic en el overlay (default: true)
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  type = "info",
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  onConfirm,
  showIcon = true,
  closeOnOverlay = true,
}) => {
  // Cerrar con tecla Escape
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case "success":
        return faCircleCheck;
      case "error":
        return faCircleExclamation;
      case "warning":
      case "confirm":
        return faTriangleExclamation;
      case "info":
      default:
        return faCircleInfo;
    }
  };

  const handleOverlayClick = (e) => {
    if (closeOnOverlay && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const modalContent = (
    <div className={styles["modal-overlay"]} onClick={handleOverlayClick}>
      <div className={`${styles["modal-container"]} ${styles[`modal-${type}`]}`}>
        <button className={styles["modal-close"]} onClick={onClose} aria-label="Cerrar">
          <FontAwesomeIcon icon={faXmark} />
        </button>

        <div className={styles["modal-body"]}>
          {showIcon && (
            <div className={`${styles["modal-icon"]} ${styles[`modal-icon-${type}`]}`}>
              <FontAwesomeIcon icon={getIcon()} />
            </div>
          )}

          {title && <h3 className={styles["modal-title"]}>{title}</h3>}

          <div className={styles["modal-content"]}>{children}</div>
        </div>

        {type === "confirm" ? (
          <div className={styles["modal-actions"]}>
            <button className={`${styles["modal-btn"]} ${styles["modal-btn-cancel"]}`} onClick={onClose}>
              {cancelText}
            </button>
            <button className={`${styles["modal-btn"]} ${styles["modal-btn-confirm"]}`} onClick={handleConfirm}>
              {confirmText}
            </button>
          </div>
        ) : (
          <div className={styles["modal-actions"]}>
            <button className={`${styles["modal-btn"]} ${styles["modal-btn-primary"]}`} onClick={onClose}>
              Aceptar
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;
